import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useConfiguration } from '@ocx/data-configuration';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useEditProfilePopup } from '@ocx/feat-edit-profile';
import { useMembership } from '@ocx/data-membership';
import { logInDev } from '@ocx/utils';
import { useConfirmationActionSheet } from '@ocx/feat-modals';

import { messages } from './order-ahead.messages';
import { useOpenOloInNewWindow } from './providers';

export const useOrderAheadNavigationOnClick = () => {
  const {
    config: { orderAhead },
  } = useConfiguration();
  const { membership } = useMembership();
  const { pushToOrderAhead } = useGetNavigationActions();
  const intl = useIntl();
  const { open: openEditProfilePopup } = useEditProfilePopup();
  const { open: openConfirmationActionSheet } = useConfirmationActionSheet();
  const { open: openOloInNewWindow } = useOpenOloInNewWindow();

  const requestMembershipComplete = useCallback(
    () =>
      openConfirmationActionSheet({
        title: intl.formatMessage(messages['order-ahead.request-profile-completion-popup.title']),
        description: intl.formatMessage(messages['order-ahead.request-profile-completion-popup.description']),
        confirmButtonText: intl.formatMessage(
          messages['order-ahead.request-profile-completion-popup.confirm-button-text'],
        ),
        onConfirm: openEditProfilePopup,
      }),
    [intl, openConfirmationActionSheet, openEditProfilePopup],
  );

  return useCallback(() => {
    if (!orderAhead.enabled) return undefined;

    // Handle Vroom provider
    if (orderAhead.provider.type === 'vroom') {
      return pushToOrderAhead();
    }

    // Handle Lula provider
    if (orderAhead.provider.type === 'lula') {
      return pushToOrderAhead();
    }

    // Handle Olo provider (when profile is completed)
    if (orderAhead.provider.type === 'olo' && membership.isCompleted) {
      return openOloInNewWindow();
    }

    // Handle Olo provider (when profile is not completed)
    if (orderAhead.provider.type === 'olo' && !membership.isCompleted) {
      requestMembershipComplete().catch(logInDev);
      return undefined;
    }

    return undefined;
  }, [orderAhead, pushToOrderAhead, membership.isCompleted, requestMembershipComplete, openOloInNewWindow]);
};
